import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import type { MuiFormSelectOptionT } from 'components/Form/MuiFormSelect/MuiFormSelect';
import { useTickets } from 'contexts/TicketsContext';
import type { GetTicketsResponseDTO } from 'declarations/dto/tickets';
import type { ParameterSortingValuesE } from 'declarations/enums/tickets';
import type { TicketI } from 'declarations/interfaces/tickets';
import type { Column } from 'declarations/types/common';
import type { SelectOptionT } from 'declarations/types/select';
import type { ChangeTicketsListFormValues } from 'pages/TicketsPage/hooks/useTicketsPage';

import { TicketTableHead } from './headers/TicketTableHead';
import { TicketTableRow } from './rows/TicketTableRow';
import {
  CHECKBOX_COLUMN_WIDTH_PX,
  FIXED_COLS_WIDTH_PX,
  VIEWED_INDICATOR_COLUMN_WIDTH_PX,
  getDataColumnWidth,
  getDataColumnWidthPx,
  getMinTableWidthPx,
  getTotalWeight,
} from './TicketsTableConfiguration';

export interface TicketsTableProps {
  ticketsPages: GetTicketsResponseDTO[] | undefined | null;
  columnOrder: Column[];
  selectedRows: string[];
  setSelectedRows: (value: string[]) => void;
  handleCheckClick: (ticketId: string, roofTopId: string) => void;
  onRowClick: (
    id: string,
    roofTopId: string,
    payload: { roNumber: string; lineNumber: string | null }
  ) => () => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  sortParameter: string;
  sortOrder: 'asc' | 'desc';
  onRequestSort: (param: ParameterSortingValuesE) => () => void;
  selectedTickets: string[];
  setSelectedTickets: (value: string[]) => void;
  loading: boolean;
  isFetchingNextTicketsPage: boolean;
  isEditMode: boolean;
  formMethods: UseFormReturn<Record<string, ChangeTicketsListFormValues>>;
  ticketPrioritiesOptions: SelectOptionT[];
  ticketPrioritiesOptionsWithIcon: MuiFormSelectOptionT[];
  ticketSubcategoriesOptions: SelectOptionT[];
  claimStatusesOptions: SelectOptionT[];
  getAssigneeOptions: (roofTopId: string) => SelectOptionT[];
  getAdvisorOptions: (roofTopId: string) => SelectOptionT[];
  getTechOptions: (roofTopId: string) => SelectOptionT[];
  isLoadingTicketPrioritiesOptions: boolean;
  isLoadingTicketSubcategoriesOptions: boolean;
  isLoadingClaimStatusesOptions: boolean;
  isMobile: boolean;
  isScheduleSection?: boolean;
  /** Rendered at bottom of scroll container for infinite scroll trigger */
  bottomSlot?: React.ReactNode;
}

export const TicketsTable: FC<TicketsTableProps> = ({
  ticketsPages,
  columnOrder,
  selectedRows,
  setSelectedRows: _setSelectedRows,
  handleCheckClick,
  onRowClick,
  onSelectAllClick,
  sortParameter,
  sortOrder,
  onRequestSort,
  selectedTickets,
  setSelectedTickets,
  loading,
  isFetchingNextTicketsPage,
  isEditMode,
  formMethods,
  ticketPrioritiesOptions,
  ticketPrioritiesOptionsWithIcon,
  ticketSubcategoriesOptions,
  claimStatusesOptions,
  getAssigneeOptions,
  getAdvisorOptions,
  getTechOptions,
  isLoadingTicketPrioritiesOptions,
  isLoadingTicketSubcategoriesOptions,
  isLoadingClaimStatusesOptions,
  isMobile,
  isScheduleSection = false,
  bottomSlot,
}) => {
  const tableBodyRef = useRef<HTMLTableSectionElement>(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const { ticketListScrollContainerRef, ticketListScrollPosition, setTicketListScrollPosition } =
    useTickets();
  const visibleColumns = columnOrder.filter((c): c is Column => c.visible !== false);
  const totalWeight = getTotalWeight(visibleColumns);

  const [containerWidthPx, setContainerWidthPx] = useState<number>(0);

  useEffect(() => {
    const el = tableWrapperRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width ?? 0;
      setContainerWidthPx(width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = tableWrapperRef.current;
    if (el && ticketListScrollPosition > 0 && !loading) {
      requestAnimationFrame(() => {
        el.scrollTop = ticketListScrollPosition;
        setTicketListScrollPosition(0);
      });
    }
  }, [ticketListScrollPosition, setTicketListScrollPosition, loading]);

  const minTableWidthPx = getMinTableWidthPx(visibleColumns);
  const minDataColsWidthPx = minTableWidthPx - FIXED_COLS_WIDTH_PX;
  const dataColumnsTotalWidthPx = Math.max(
    minDataColsWidthPx,
    Math.max(0, containerWidthPx - FIXED_COLS_WIDTH_PX)
  );
  const dataColumnWidthsPx =
    dataColumnsTotalWidthPx > 0
      ? visibleColumns.map(col =>
          getDataColumnWidthPx(col.label, totalWeight, dataColumnsTotalWidthPx)
        )
      : null;

  const tableMinWidthPx =
    dataColumnWidthsPx != null
      ? FIXED_COLS_WIDTH_PX + dataColumnWidthsPx.reduce((a, b) => a + b, 0)
      : minTableWidthPx;

  const flatTickets: TicketI[] =
    ticketsPages?.flatMap(page => page.data ?? []).filter((t): t is TicketI => t != null) ?? [];
  const rowCount = flatTickets.length;

  const handleClick = (
    _event: React.MouseEvent<unknown>,
    id: string,
    roofTopId: string,
    roNumber: string,
    lineNumber: string | null
  ): void => {
    onRowClick(id, roofTopId, { roNumber, lineNumber })();
  };

  return (
    <Box
      mt={2}
      sx={{
        flex: 1,
        minHeight: 0,
        width: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Scroll container – same structure as WarrQueue WorkItemsTable for sticky headers */}
      <Box
        ref={(el: HTMLDivElement | null): void => {
          (tableWrapperRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
          (ticketListScrollContainerRef as React.MutableRefObject<HTMLDivElement | null>).current =
            el;
        }}
        sx={{
          width: '100%',
          height: '100%',
          overflowY: 'auto',
          overflowX: isMobile ? 'hidden' : 'auto',
          pb: 16,
          minWidth: isMobile ? '100%' : tableMinWidthPx,
          ...(isMobile && {
            display: 'flex',
            flexDirection: 'column',
            gap: 0.25,
            padding: '0 4px',
          }),
        }}
      >
        {isMobile ? (
          <Table
            sx={(theme): object => ({
              width: '100%',
              '& tbody tr': {
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gridAutoFlow: 'dense',
                gap: '6px 6px',
                padding: '8px 6px',
                marginBottom: 8,
                borderRadius: 2,
                overflow: 'visible',
                boxShadow: 1,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                [theme.breakpoints.down(426)]: {
                  gridTemplateColumns: '1fr',
                  gap: '6px 4px',
                  padding: '6px 6px',
                  marginBottom: 6,
                },
                '& td': {
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px 3px',
                  border: 'none',
                  boxShadow: 'none',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                  overflow: 'visible',
                  '&:first-of-type': { borderTopLeftRadius: 6, borderTopRightRadius: 6 },
                  '&:last-of-type': { borderBottomLeftRadius: 6, borderBottomRightRadius: 6 },
                  [theme.breakpoints.down(426)]: {
                    padding: '4px 3px',
                  },
                },
                '& td[data-label]': {
                  whiteSpace: 'normal',
                },
                '& td[data-label="Select"]': {
                  minWidth: 100,
                  flexShrink: 0,
                  overflow: 'visible',
                  gridColumn: '1 / -1',
                  justifyContent: 'flex-start',
                },
                '& td[data-label]::before': {
                  content: 'attr(data-label)',
                  fontWeight: 600,
                  flexShrink: 0,
                  width: 72,
                  minWidth: 72,
                  marginRight: 4,
                  fontSize: '0.75rem',
                  display: 'inline-block',
                  textAlign: 'left',
                  lineHeight: 1.3,
                  overflow: 'visible',
                  [theme.breakpoints.down(426)]: {
                    width: 60,
                    minWidth: 60,
                    marginRight: 3,
                    fontSize: '0.7rem',
                  },
                },
                '& td[data-label="P"], & td[data-label="Line"]': {
                  gridColumn: 1,
                },
                '& td[data-label="P"] > *': {
                  flex: '0 0 32px',
                  minWidth: 32,
                  width: 32,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                },
                '& td[data-label="Status"], & td[data-label="Age"], & td[data-label="Claim Age"], & td[data-label="Closed RO Age"]':
                  {
                    gridColumn: 2,
                    '&::before': {
                      width: 72,
                      minWidth: 72,
                      textAlign: 'left',
                      [theme.breakpoints.down(426)]: {
                        width: 60,
                        minWidth: 60,
                      },
                    },
                    '& > *': {
                      flex: '0 0 32px',
                      minWidth: 32,
                      width: 32,
                      textAlign: 'right',
                    },
                  },
                '& td[data-label="Subcategory"]': {
                  gridColumn: 2,
                  '&::before': {
                    width: 72,
                    minWidth: 72,
                    textAlign: 'left',
                    [theme.breakpoints.down(426)]: {
                      width: 60,
                      minWidth: 60,
                    },
                  },
                  '& > *': {
                    minWidth: 32,
                    textAlign: 'right',
                  },
                },
                /* Edit mode: standardize input sizes for card layout */
                '& td[data-label]:has(.MuiFormControl-root) > *, & td[data-label]:has(.MuiTextField-root) > *':
                  {
                    flex: 1,
                    minWidth: 0,
                    maxWidth: '100%',
                  },
                '& td[data-label="P"]:has(.MuiFormControl-root) > *': {
                  flex: '1 1 auto',
                  minWidth: 0,
                  maxWidth: 80,
                },
                '& td[data-label] .MuiFormControl-root, & td[data-label] .MuiTextField-root': {
                  width: '100%',
                  minWidth: 0,
                  maxWidth: '100%',
                  overflow: 'visible',
                },
                '& td[data-label] .MuiInputBase-root': {
                  minHeight: 28,
                  fontSize: '0.75rem',
                  '& .MuiOutlinedInput-notchedOutline': { borderRadius: 1 },
                },
                '& td[data-label] .MuiInputBase-input, & td[data-label] .MuiSelect-select': {
                  padding: '4px 8px',
                },
                '& td[data-label] .MuiInputLabel-root': {
                  fontSize: '0.75rem',
                  overflow: 'visible',
                  whiteSpace: 'nowrap',
                },
                '& td[data-label] .MuiSelect-select, & td[data-label] .MuiInputBase-input': {
                  fontSize: '0.75rem',
                },
                '& td[data-label="Last Comment"]': {
                  gridColumn: '1 / -1',
                  alignItems: 'flex-start',
                },
                '& td[data-label="Last Comment"] .MuiInputBase-root': {
                  minHeight: 36,
                  alignItems: 'flex-start',
                },
              },
            })}
          >
            <TableBody ref={tableBodyRef} data-testid="tickets-list">
              {flatTickets.map(row => {
                const isItemSelected = selectedRows.includes(row.id);
                return (
                  <TicketTableRow
                    key={row.id}
                    row={row}
                    columnOrder={columnOrder}
                    isItemSelected={isItemSelected}
                    handleClick={handleClick}
                    handleCheckClick={handleCheckClick}
                    isEditMode={isEditMode}
                    selectedTickets={selectedTickets}
                    formMethods={formMethods}
                    ticketPrioritiesOptions={ticketPrioritiesOptions}
                    ticketPrioritiesOptionsWithIcon={ticketPrioritiesOptionsWithIcon}
                    ticketSubcategoriesOptions={ticketSubcategoriesOptions}
                    claimStatusesOptions={claimStatusesOptions}
                    getAssigneeOptions={getAssigneeOptions}
                    getAdvisorOptions={getAdvisorOptions}
                    getTechOptions={getTechOptions}
                    isLoadingTicketPrioritiesOptions={isLoadingTicketPrioritiesOptions}
                    isLoadingTicketSubcategoriesOptions={isLoadingTicketSubcategoriesOptions}
                    isLoadingClaimStatusesOptions={isLoadingClaimStatusesOptions}
                    isMobile={isMobile}
                    isScheduleSection={isScheduleSection}
                    dataLabelForCard={true}
                  />
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <Table
            sx={{
              tableLayout: 'fixed',
              width: '100%',
              minWidth: tableMinWidthPx,
              '& .MuiTableCell-root': {
                px: 1,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              },
            }}
            aria-labelledby="tableTitle"
            size="medium"
          >
            <colgroup>
              <col style={{ width: CHECKBOX_COLUMN_WIDTH_PX }} />
              <col style={{ width: VIEWED_INDICATOR_COLUMN_WIDTH_PX }} />
              {dataColumnWidthsPx
                ? visibleColumns.map((col, i) => (
                    <col key={col.id} style={{ width: dataColumnWidthsPx[i] }} />
                  ))
                : visibleColumns.map(col => (
                    <col
                      key={col.id}
                      style={{ width: getDataColumnWidth(col.label, totalWeight) }}
                    />
                  ))}
            </colgroup>
            <TicketTableHead
              columnOrder={columnOrder}
              dataColumnWidthsPx={dataColumnWidthsPx}
              totalWeight={totalWeight}
              numSelected={selectedRows.length}
              rowCount={rowCount}
              onSelectAllClick={onSelectAllClick}
              sortParameter={sortParameter}
              sortOrder={sortOrder}
              onRequestSort={onRequestSort}
              selectedTickets={selectedTickets}
              setSelectedTickets={setSelectedTickets}
            />
            <TableBody ref={tableBodyRef} data-testid="tickets-list">
              {flatTickets.map(row => {
                const isItemSelected = selectedRows.includes(row.id);
                return (
                  <TicketTableRow
                    key={row.id}
                    row={row}
                    columnOrder={columnOrder}
                    isItemSelected={isItemSelected}
                    handleClick={handleClick}
                    handleCheckClick={handleCheckClick}
                    isEditMode={isEditMode}
                    selectedTickets={selectedTickets}
                    formMethods={formMethods}
                    ticketPrioritiesOptions={ticketPrioritiesOptions}
                    ticketPrioritiesOptionsWithIcon={ticketPrioritiesOptionsWithIcon}
                    ticketSubcategoriesOptions={ticketSubcategoriesOptions}
                    claimStatusesOptions={claimStatusesOptions}
                    getAssigneeOptions={getAssigneeOptions}
                    getAdvisorOptions={getAdvisorOptions}
                    getTechOptions={getTechOptions}
                    isLoadingTicketPrioritiesOptions={isLoadingTicketPrioritiesOptions}
                    isLoadingTicketSubcategoriesOptions={isLoadingTicketSubcategoriesOptions}
                    isLoadingClaimStatusesOptions={isLoadingClaimStatusesOptions}
                    isMobile={isMobile}
                    isScheduleSection={isScheduleSection}
                  />
                );
              })}
            </TableBody>
          </Table>
        )}
        {loading && !isFetchingNextTicketsPage && (
          <Typography textAlign="center" mt={3} variant="body1">
            Loading Tickets... <CircularProgress size={16} />
          </Typography>
        )}
        {bottomSlot}
      </Box>
    </Box>
  );
};
